// Reads gzipped-NDJSON `.ams` shard tiles from the tenant's blob container via a short-lived read-only SAS capsule.
/* global DecompressionStream */
import { LruCache } from '@/utils/LruCache.js'
import ApiServer from '@/utils/api/ApiServer.js'
import { createResourceUrl, sasNeedsRefresh } from '@/utils/SasHelpers.js'

export const SHARD_ZOOM = 11

export const MAX_TILES = 24

const SPECIES_CAPSULE_ENDPOINT = 'companyinfo/blobinfo/species'

const emptyCollection = () => ({ type: 'FeatureCollection', features: [] })

// Caches the in-flight/resolved promise per tile so concurrent panners share one fetch.
const TILE_CACHE_CAPACITY = 300
const tileCache = new LruCache(TILE_CACHE_CAPACITY)

let capsule = null
let capsulePromise = null

async function getCapsule () {
  if (capsule && !sasNeedsRefresh(capsule.blobSasUri)) return capsule
  if (!capsulePromise) {
    capsulePromise = ApiServer.get(SPECIES_CAPSULE_ENDPOINT)
      .then(res => {
        const next = res && res.data && res.data[0]
        if (!next || !next.blobSasUri) throw new Error('No species SAS capsule returned')
        capsule = next
        return next
      })
      .finally(() => { capsulePromise = null })
  }
  return capsulePromise
}

// `.ams` shards are raw gzip with no Content-Encoding, so detect the magic bytes and decompress manually.
async function gunzipToText (buffer) {
  const bytes = new Uint8Array(buffer)
  const isGzip = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b
  if (!isGzip) return new TextDecoder().decode(buffer)
  const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream('gzip'))
  return new Response(stream).text()
}

function parseShard (text) {
  const features = []
  for (const line of text.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed) continue
    try {
      features.push(JSON.parse(trimmed))
    } catch (e) {
      // drop a malformed line rather than the whole shard
    }
  }
  return { type: 'FeatureCollection', features }
}

export function lonLatToTile (lon, lat, z = SHARD_ZOOM) {
  const n = 2 ** z
  const latRad = lat * Math.PI / 180
  const x = Math.floor((lon + 180) / 360 * n)
  const y = Math.floor((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n)
  const clamp = v => Math.max(0, Math.min(n - 1, v))
  return { z, x: clamp(x), y: clamp(y) }
}

// Azure Maps camera bounds are [west, south, east, north].
// `padding` expands the tile window by that many tiles on every side
export function tilesForBounds (bounds, z = SHARD_ZOOM, padding = 0) {
  const [west, south, east, north] = bounds
  const topLeft = lonLatToTile(west, north, z)
  const bottomRight = lonLatToTile(east, south, z)
  const n = 2 ** z
  const clamp = v => Math.max(0, Math.min(n - 1, v))
  const tiles = []
  for (let x = clamp(topLeft.x - padding); x <= clamp(bottomRight.x + padding); x++) {
    for (let y = clamp(topLeft.y - padding); y <= clamp(bottomRight.y + padding); y++) {
      tiles.push({ z, x, y })
    }
  }
  return tiles
}

// Rejects on any failure other than 404 (missing shard = genuinely empty tile). Failed fetches are
// evicted from the cache so a transient SAS/network error doesn't poison the tile for the session.
export function fetchTileStrict (z, x, y) {
  const key = `${z}/${x}/${y}`
  const cached = tileCache.get(key)
  if (cached) return cached
  const request = (async () => {
    const cap = await getCapsule()
    const url = createResourceUrl(cap.blobSasUri, cap.container, `${cap.location}/${z}/${x}/${y}.ams`)
    const res = await fetch(url)
    if (res.status === 404) return emptyCollection()
    if (!res.ok) throw new Error(`Angel Map Shards tile ${key} -> ${res.status}`)
    return parseShard(await gunzipToText(await res.arrayBuffer()))
  })()
  tileCache.set(key, request)
  request.catch(() => tileCache.delete(key))
  return request
}

// Lenient variant for map rendering: a failed tile renders as empty rather than breaking the pan.
export function fetchTile (z, x, y) {
  return fetchTileStrict(z, x, y).catch(e => {
    console.warn(`Angel Map Shards fetch failed for tile ${z}/${x}/${y}`, e)
    return emptyCollection()
  })
}
